// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { productLineV2Props } from "@store/Themes/common/props/productLineV2";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

export const productLineStatusAboveOverlayZIndex = 20;

export const getIsDisabledProductLineStatus = (status: ProductLineStatusType | undefined) => {
    if (!status) {
        return false;
    }

    return productLineStatus[status]?.disabled ?? false;
};

export interface ProductLineStatus {
    backgroundColor: string;
    color: string;
    label: string;
    disabled?: boolean;
}

export interface ProductLineStatusTypes {
    added: ProductLineStatus;
    canceled: ProductLineStatus;
    modified: ProductLineStatus;
}

export type ProductLineStatusType = keyof ProductLineStatusTypes;

export const productLineStatus: ProductLineStatusTypes = {
    added: {
        backgroundColor: "info",
        color: "info.contrast",
        label: "Added",
    },
    canceled: {
        backgroundColor: "danger",
        color: "danger.contrast",
        disabled: true,
        label: "Canceled",
    },
    modified: {
        backgroundColor: "warning",
        color: "warning.contrast",
        label: "Modified",
    },
};

export interface ProductLineStatusOverlayProps {
    type: ProductLineStatusType | undefined;
}

export interface ProductLineStatusOverlayStyles {
    wrapper?: InjectableCss;
    label?: InjectableCss;
}

export const productLineStatusOverlayStyles: ProductLineStatusOverlayStyles = {
    wrapper: {
        css: css`
            position: absolute;
            top: -${productLineV2Props.wrapperBorderWidth};
            right: -${productLineV2Props.wrapperBorderWidth};
            bottom: -${productLineV2Props.wrapperBorderWidth};
            left: -${productLineV2Props.wrapperBorderWidth};
            overflow: hidden;
            border-radius: inherit;

            &::before,
            &::after {
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
            }

            &::before {
                content: "";
                z-index: 99;
                border-width: 2px;
                border-style: solid;
                border-color: inherit;
                border-radius: inherit;
                pointer-events: none;
            }

            &::after {
                z-index: 10;
                background-color: ${getColor("common.background")};
                opacity: 0.5;
            }
        `,
    },
    label: {
        css: css`
            position: absolute;
            top: 0;
            left: 0;
            z-index: 99;
            padding: ${pxToRem(3, 8)};
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.medium};
            border-bottom-right-radius: ${pxToRem(2)};
            pointer-events: auto;
        `,
    },
};

const ProductLineStatusOverlay: FC<ProductLineStatusOverlayProps> = ({ type }) => {
    if (!type) {
        return null;
    }

    const { backgroundColor, color, label, disabled } = productLineStatus[type];

    const styles = useMemo(() => {
        return mergeProps<ProductLineStatusOverlayStyles>(productLineStatusOverlayStyles, {
            wrapper: {
                css: css`
                    border-color: ${getColor(backgroundColor)};

                    ${() => {
                        return disabled
                            ? css`
                                  &::after {
                                      content: "";
                                  }
                              `
                            : css`
                                  pointer-events: none;
                              `;
                    }}
                `,
            },
            label: {
                css: css`
                    background-color: ${getColor(backgroundColor)};
                    color: ${getColor(color)};
                `,
            },
        });
    }, [backgroundColor, color, disabled]);

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.label} data-test-selector={`productLineStatus_${type}`}>
                {translate(`${label}`)}
            </Typography>
        </StyledWrapper>
    );
};

export default ProductLineStatusOverlay;
